/**
 * User Profile Component
 * 
 * Simplified user profile with:
 * - User details (name, photo)
 * - Points display with explanation
 * - Logout button
 * - Change name functionality
 * 
 * iOS-styled design with mobile optimization
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../firebase/AuthContext';
import { getUserGameHistory } from '../../firebase/gameService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './UserProfile.css';

/**
 * User Profile Component
 * Simplified iOS-like design with essential functionality only
 * 
 * @returns {JSX.Element} User profile component
 */
function UserProfile() {
  // User state
  const { currentUser, logout } = useAuth();
  
  // Profile data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Removed unused gameHistory state, but we still fetch it in loadProfileData
  // for potential future use in displaying user's game history
  const [userPoints, setUserPoints] = useState(0);
  const [newName, setNewName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  /**
   * Load user profile data on component mount
   * Fetches total points directly from user document and game history
   */
  useEffect(() => {
    const loadProfileData = async () => {
      if (!currentUser) return;
      
      try {
        console.log("Loading profile data for user:", currentUser.uid);
        setLoading(true);
        setError(''); // Clear previous errors
        
        // Fetch user document to get accurate total points
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserPoints(userData.totalPoints || 0);
          console.log("User total points from document:", userData.totalPoints || 0);
        } else {
          console.log("No user document found!");
          setUserPoints(0);
        }
        
        // Fetch game history for display purposes
        try {
          // Fetch recent game history 
          const history = await getUserGameHistory(currentUser.uid, 20);
          console.log("Loaded game history:", history.length, "games");
        } catch (historyErr) {
          console.error('Error loading game history:', historyErr);
          // Continue with empty history rather than failing completely
        }
      } catch (err) {
        console.error('Error loading profile data:', err);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadProfileData();
  }, [currentUser]);
  
  /**
   * Handle user logout
   */
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Logout failed. Please try again.');
    }
  };
  
  /**
   * Enable name editing mode
   */
  const handleEditName = () => {
    setNewName(currentUser.displayName || '');
    setIsEditing(true);
    setUpdateSuccess(false);
  };
  
  /**
   * Save updated user name
   */
  const handleUpdateName = async () => {
    if (!newName.trim()) {
      return;
    }
    
    try {
      await currentUser.updateProfile({
        displayName: newName
      });
      setIsEditing(false);
      setUpdateSuccess(true);
      
      // Reset success message after delay
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to update name:', error);
      setError('Failed to update name. Please try again.');
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile data...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="profile-container">
        <div className="profile-error">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          {currentUser.photoURL ? (
            <img 
              src={currentUser.photoURL} 
              alt="Profile" 
              className="avatar-image"
            />
          ) : (
            <div className="avatar-placeholder">
              {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
        </div>
        
        <div className="profile-info">
          {isEditing ? (
            <div className="name-edit-form">
              <input 
                type="text" 
                value={newName} 
                onChange={(e) => setNewName(e.target.value)}
                className="name-input"
                placeholder="Enter your name"
                autoFocus
              />
              <div className="edit-buttons">
                <button 
                  onClick={() => setIsEditing(false)} 
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateName} 
                  className="save-button"
                  disabled={!newName.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="name-display">
                <h1 className="profile-name">
                  {currentUser.displayName || 'User'}
                </h1>
                <button 
                  onClick={handleEditName} 
                  className="edit-name-button"
                  aria-label="Edit name"
                >
                  ✏️
                </button>
              </div>
              {updateSuccess && <p className="update-success">Name updated successfully!</p>}
            </>
          )}
        </div>
      </div>
      
      {/* Points Display */}
      <section className="profile-section points-section">
        <div className="points-display">
          <div className="points-value">{userPoints}</div>
          <div className="points-label">Points</div>
        </div>
      </section>
      
      {/* Points Explanation Section */}
      <section className="profile-section points-explanation-section">
        <div className="points-explanation-card">
          <h3 className="points-explanation-title">How Points Are Calculated</h3>
          <div className="points-explanation-content">
            <p>Each question has a maximum of 4 sub-questions:</p>
            <ul className="points-explanation-list">
              <li><span className="point-category">Author:</span> 1 point</li>
              <li><span className="point-category">Publication Year:</span> 1 point</li>
              <li><span className="point-category">Genre:</span> 1 point</li>
              <li><span className="point-category">Literary Age:</span> 1 point</li>
            </ul>
            <p className="points-explanation-summary">
              You earn 1 point for each correct answer, with a maximum of 4 points per literary work.
            </p>
          </div>
        </div>
      </section>
      
      {/* Logout Button */}
      <section className="profile-section logout-section">
        <button 
          onClick={handleLogout} 
          className="logout-button"
        >
          Logout
        </button>
      </section>
    </div>
  );
}

export default UserProfile; 